import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';

function Line({ points, color, label, fmt }) {
  if (!points || points.length < 2) {
    return <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>{label}: not enough history yet</div>;
  }
  const ys = points.map((p) => p.v);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const span = max - min || 1;
  const W = 600;
  const H = 60;
  const d = points.map((p, i) => {
    const x = (i / (points.length - 1)) * W;
    const y = H - ((p.v - min) / span) * (H - 8) - 4;
    return (i ? 'L' : 'M') + x.toFixed(1) + ' ' + y.toFixed(1);
  }).join(' ');
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 2 }}>
        <span>{label}</span>
        <span>{fmt(points[0].v)} → <b style={{ color }}>{fmt(points[points.length - 1].v)}</b> (min {fmt(min)} · max {fmt(max)})</span>
      </div>
      <svg viewBox={'0 0 ' + W + ' ' + H} preserveAspectRatio="none" style={{ width: '100%', height: 60, display: 'block' }}>
        <path d={d} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

// Proyeksi hanya ditampilkan kalau rentang data cukup panjang. Dengan data 1-2 hari,
// laju MB/hari didominasi fluktuasi harian dan angka "habis dalam X hari" menyesatkan.
const MIN_DAYS_FOR_FORECAST = 3;

function forecast(rows) {
  if (rows.length < 2) return null;
  const first = rows[0];
  const last = rows[rows.length - 1];
  const days = (new Date(last.recorded_at) - new Date(first.recorded_at)) / 86400000;
  if (days < MIN_DAYS_FOR_FORECAST) return { days, tooShort: true };
  const used = Number(first.free_mb) - Number(last.free_mb);
  const perDay = used / days;
  const freeNow = Number(last.free_mb);
  return {
    days,
    perDay,
    daysLeft: perDay > 0 ? freeNow / perDay : null,
    freeNow,
  };
}

export default function AseSpaceTrend({ connectionId, keys, refreshKey }) {
  const [key, setKey] = useState('P01|data');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    if (!connectionId || !key) return undefined;
    setData(null);
    setErr('');
    api.get('/api/sap/basis/ase-space-history', { params: { connectionId, key, days } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr((e && e.response && e.response.data && e.response.data.error) || String(e.message || e)); });
    return () => { alive = false; };
  }, [connectionId, key, days, refreshKey]);

  const rows = (data && data.rows) || [];
  const opts = (keys && keys.length ? keys : (data && data.keys) || []);
  const num = (v) => Number(v || 0).toLocaleString('en-US');
  const sel = { padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12.5, background: 'transparent', color: 'inherit' };
  const f = forecast(rows);

  return (
    <div style={{ marginTop: 18, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>Space trend</span>
        <select style={sel} value={key} onChange={(e) => setKey(e.target.value)}>
          {opts.map((o) => <option key={o} value={o}>{o.replace('|', ' / ')}</option>)}
        </select>
        <select style={sel} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>{d} days</option>)}
        </select>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{data ? rows.length + ' snapshots' : (err ? '' : 'loading...')}</span>
      </div>
      {err ? <div style={{ fontSize: 12.5, color: '#dc2626' }}>{err}</div> : (
        <>
          <Line points={rows.map((r) => ({ v: Number(r.used_pct || 0) }))} color="#0891b2" label="% Used" fmt={(v) => v.toFixed(2) + '%'} />
          <Line points={rows.map((r) => ({ v: Number(r.free_mb || 0) }))} color="#b45309" label="Free MB" fmt={num} />
          {f && (
            <div style={{ fontSize: 12.5, marginTop: 4, color: '#64748b' }}>
              {f.tooShort
                ? 'Growth forecast needs at least ' + MIN_DAYS_FOR_FORECAST + ' days of history (have ' + f.days.toFixed(1) + ').'
                : (f.perDay > 0
                  ? 'Growth ' + num(Math.round(f.perDay)) + ' MB/day over ' + f.days.toFixed(1) + ' days · '
                    + num(Math.round(f.freeNow)) + ' MB free → '
                    + '~' + Math.round(f.daysLeft) + ' days until full at this rate'
                  : 'No net growth over ' + f.days.toFixed(1) + ' days (free space stable or increasing).')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
